import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import InsightBotWidget from '../insightbot/InsightBotWidget';
import { getUnreadNotificationCount } from '../../services/api';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import clsx from 'clsx';
import './AppLayout.css';

function AppLayout({ children }) {
  const { token } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data?.data?.unread_count ?? 0);
    } catch {
      setUnreadCount(0);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, 60000);
    return () => window.clearInterval(intervalId);
  }, [token, loadUnreadCount]);

  useEffect(() => {
    if (!token) return undefined;
    // Use the API base URL that was already computed by determineApiBaseUrl() in api.js.
    // That function always mirrors window.location.protocol, so on HTTPS pages the
    // WebSocket will use wss:// instead of the old hardcoded http:// fallback which
    // would produce ws:// and be blocked as mixed content.
    const apiBase = api.defaults.baseURL || window.location.origin;
    const wsBase = apiBase.replace(/^http/, 'ws').replace(/\/+$/, '').replace(/\/api(?:\/v1)?$/i, '');
    const socket = new WebSocket(`${wsBase}/ws/notifications?token=${encodeURIComponent(token)}`);
    socketRef.current = socket;

    socket.onmessage = (event) => {
      setUnreadCount((c) => c + 1);
      loadUnreadCount();
      try {
        const data = JSON.parse(event.data);
        if (data?.type === 'notification') {
          toast(data.message || 'You have new notifications', { icon: '🔔' });
        }
      } catch {
        toast('You have new notifications', { icon: '🔔' });
      }
    };

    socket.onerror = () => { };

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [token, loadUnreadCount]);

  const toggleSidebar = () => setSidebarOpen((o) => !o);
  const closeSidebar = () => setSidebarOpen(false);
  const toggleCollapse = () => setIsCollapsed((c) => !c);

  return (
    <div className={clsx('app-layout', { 'app-layout--collapsed': isCollapsed })}>
      <TopBar />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapse}
        notificationData={{ unreadCount }}
      />
      <main className="app-main" id="main-content" tabIndex={-1}>
        <div className="app-main__inner">
          {children}
        </div>
      </main>
      <InsightBotWidget />
    </div>
  );
}

export default AppLayout;